/**
 * Player detail — one aggregated view per user for the admin.
 *
 * Bound to `GET /admin/metrics/user/{id}` (added for admin v2). Renders
 * the hero card, balances, inventory, duel stats + game record and the
 * live-trivia honours list. The blacklist toggle re-uses the existing
 * /admin/users/{id}/blacklist endpoints so state stays consistent with
 * the list screen.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  adminExtendRetest,
  adminGetRetest,
  adminListRetests,
  adminReviewRetest,
  adminSendRetest,
  datetimeLocalToIso,
  formatLagosDeadline,
  isoAtHoursFromNow,
} from '../../lib/retest.js';
import {
  errorState,
  escape,
  fmtBps,
  fmtInt,
  fmtKobo,
  fmtMonthYear,
  fmtRelTime,
  skeletonGrid,
} from './common.js';

export async function renderPlayerDetail(host, ctx) {
  if (!ctx.playerId) {
    host.innerHTML = errorState('Missing player id.');
    return;
  }
  host.innerHTML = skeletonGrid(4);
  try {
    const data = await api(`/admin/metrics/user/${encodeURIComponent(ctx.playerId)}`);
    host.innerHTML = _render(data, ctx);
    _wire(host, ctx, data);
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) {
        ctx.navigate('/login');
        return;
      }
      if (err.status === 404) {
        host.innerHTML = `<div class="adm2-error">User not found.</div>
          <a href="/admin/v2/players" data-link class="adm2-btn" style="margin-top:12px;display:inline-block;">← Back to users</a>`;
        return;
      }
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

function _render(d, ctx) {
  const id = d.identity || {};
  const bal = d.balances || {};
  const inv = d.inventory || {};
  const du = d.duels || {};
  const li = d.live || {};
  const sp = d.spend || {};
  const ea = d.earnings || {};

  const name = id.username || (id.email || '').split('@')[0] || 'Player';
  const initial = (name.charAt(0) || '?').toUpperCase();
  const handle = id.username ? `@${id.username}` : id.email;
  const actLabel = id.is_admin ? 'Admin · protected' : id.is_blacklisted ? 'Unblacklist' : 'Blacklist';
  const actDisabled = id.is_admin;
  const stateBadge = id.account_state && id.account_state !== 'active'
    ? id.account_state === 'banned'
      ? '<span class="adm2-hero-badge" style="background:rgba(248,113,113,.28);color:#fff;">BANNED</span>'
      : '<span class="adm2-hero-badge" style="background:rgba(251,191,36,.24);color:#fff;">UNDER INVESTIGATION</span>'
    : '';
  const badges = [
    id.is_admin ? '<span class="adm2-hero-badge">ADMIN</span>' : '',
    id.is_blacklisted ? '<span class="adm2-hero-badge" style="background:rgba(248,113,113,.24);">BLACKLISTED</span>' : '',
    stateBadge,
    `<span class="adm2-hero-badge">SINCE ${escape(fmtMonthYear(id.created_at))}</span>`,
  ].filter(Boolean).join('');

  const honours = li.honours && li.honours.length
    ? li.honours
        .map(
          (h) => `
      <div style="display:flex;align-items:center;gap:14px;padding:13px 15px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:12px;margin-bottom:10px;">
        <div style="width:38px;height:38px;border-radius:10px;background:rgba(233,199,126,.14);border:1px solid var(--a-warm-bd);display:flex;align-items:center;justify-content:center;font:700 16px 'IBM Plex Mono',monospace;color:var(--a-warm-ink);flex:none;">${_rankEmoji(h.rank)}</div>
        <div style="flex:1;min-width:0;">
          <div style="font:600 14px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(h.title || 'Live trivia')}</div>
          <div style="font:400 12px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:2px;">rank ${escape(fmtInt(h.rank))} · ${escape(fmtRelTime(h.finished_at))}${h.prize_kobo != null ? ' · ' + escape(fmtKobo(h.prize_kobo)) : ''}</div>
        </div>
      </div>
    `,
        )
        .join('')
    : `<div style="text-align:center;padding:24px;font:400 13px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">No trivia honours yet.</div>`;

  return `
    <a href="/admin/v2/players" data-link class="adm2-btn" style="display:inline-flex;align-items:center;gap:8px;margin-bottom:18px;text-decoration:none;">‹ All users</a>

    <div class="adm2-hero">
      <div style="position:absolute;right:-50px;top:-70px;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(255,255,255,.20),transparent 65%);"></div>
      <div class="adm2-hero-row">
        <div class="adm2-hero-avatar">${escape(initial)}</div>
        <div style="flex:1;min-width:220px;">
          <div class="adm2-hero-name">${escape(name)}</div>
          <div class="adm2-hero-sub">
            <span style="font-family:'IBM Plex Mono',monospace;">${escape(handle)}</span>
            ${id.online ? `<span class="adm2-online"><i></i>online</span>` : ''}
          </div>
          <div class="adm2-hero-badges">${badges}</div>
        </div>
        <button type="button" class="adm2-hero-btn" id="adm2-user-blacklist" data-user-id="${escape(id.id)}"
                data-action="${id.is_blacklisted ? 'remove' : 'add'}"
                ${actDisabled ? 'disabled style="opacity:.5;cursor:not-allowed;"' : ''}>
          ${escape(actLabel)}
        </button>
      </div>
    </div>

    ${_accountStatePanel(id)}
    ${_retestPanel(id, d)}

    <div class="adm2-section-label" style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <span>Balances &amp; inventory</span>
      <button type="button" id="adm2-grant-powerup-btn" data-user-id="${escape(id.id)}"
              data-user-email="${escape(id.email)}"
              style="padding:6px 12px;font-size:12px;font-weight:600;border-radius:6px;background:rgba(139,107,242,0.20);border:1px solid rgba(139,107,242,0.55);color:#B7A5F5;cursor:pointer;">
        🎁 Grant power-up
      </button>
    </div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));">
      ${_smallStat('Wallet balance', fmtKobo(bal.wallet_kobo))}
      ${_smallStatWarm('Bonus balance', fmtKobo(bal.bonus_kobo))}
      ${_smallStat('50/50 owned', fmtInt(inv.fiftyfifty_balance))}
      ${_smallStat('Erasers owned', fmtInt(inv.eraser_balance))}
    </div>

    ${_bonusPowerupPanel(id, bal)}

    <div class="adm2-section-label">Lifetime spend</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));">
      ${_smallStat('Deposits', fmtKobo(sp.deposits_kobo), 'successful only')}
      ${_smallStat('Power-up spend', fmtKobo(sp.powerup_spend_kobo), 'erasers + 50/50')}
      ${_smallStat('Eraser spend', fmtKobo(sp.eraser_spend_kobo))}
      ${_smallStat('50/50 spend', fmtKobo(sp.fiftyfifty_spend_kobo))}
    </div>

    <div class="adm2-section-label">Lifetime earnings</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));">
      ${_earnStat('Total earnings', fmtKobo(ea.total_kobo), 'duel prizes + live payouts')}
      ${_smallStat('Duel prizes', fmtKobo(ea.duel_kobo), 'from 1-v-1 wins')}
      ${_smallStat('Live payouts', fmtKobo(ea.live_kobo), 'from live trivia')}
    </div>

    <div class="adm2-section-label">Game stats</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(170px,1fr));">
      ${_center('Games', fmtInt(du.games))}
      ${_center('Win rate', fmtBps(du.win_rate_bps))}
      ${_center('Best streak', fmtInt(du.best_streak))}
      ${_center('Accuracy', fmtBps(du.accuracy_bps))}
    </div>

    <div class="adm2-stat-strip">
      <div>
        <div class="adm2-stat-strip-value" style="color:var(--a-ok);">${escape(fmtInt(du.wins))}–${escape(fmtInt(du.losses))}</div>
        <div class="adm2-stat-strip-label">1-v-1 record</div>
      </div>
      <div>
        <div class="adm2-stat-strip-value" style="color:#C9A24B;">🏆 ${escape(fmtInt(li.trivia_wins))}</div>
        <div class="adm2-stat-strip-label">Trivia wins</div>
      </div>
      <div>
        <div class="adm2-stat-strip-value">${escape(fmtInt(du.best_score))}</div>
        <div class="adm2-stat-strip-label">Best score</div>
      </div>
    </div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
        <span class="adm2-panel-title">Trivia honours</span>
        <span style="color:#C9A24B;">🏆</span>
      </div>
      ${honours}
    </div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div class="adm2-panel-title">Identity</div>
      <div class="adm2-panel-sub">from /admin/metrics/user/{id}</div>
      <div style="margin-top:14px;display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:14px;">
        ${_kv('User ID', id.id)}
        ${_kv('Email', id.email)}
        ${_kv('Status', id.status || '—')}
        ${_kv('Verified', id.email_verified ? 'yes' : 'no')}
        ${_kv('Created', fmtRelTime(id.created_at))}
        ${_kv('Last seen', fmtRelTime(id.last_seen_at))}
      </div>
    </div>
  `;
}

function _smallStat(label, value, hint) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 28px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(value)}</div>
      ${hint ? `<div style="font:500 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:6px;">${escape(hint)}</div>` : ''}
    </div>
  `;
}
function _earnStat(label, value, hint) {
  return `
    <div style="position:relative;overflow:hidden;background:var(--a-panel);border:1px solid var(--a-ok-bd);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--a-ok);"></div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 28px 'IBM Plex Mono',monospace;color:var(--a-ok);margin-top:11px;">${escape(value)}</div>
      ${hint ? `<div style="font:500 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:6px;">${escape(hint)}</div>` : ''}
    </div>
  `;
}
function _smallStatWarm(label, value) {
  return `
    <div style="position:relative;overflow:hidden;background:var(--a-warm);border:1px solid var(--a-warm-bd);border-radius:14px;padding:18px;">
      <div style="position:absolute;left:0;top:0;bottom:0;width:4px;background:linear-gradient(#C9A24B,#A8842F);"></div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-warm-ink-2);">${escape(label)}</div>
      <div style="font:600 28px 'IBM Plex Mono',monospace;color:var(--a-warm-ink);margin-top:11px;">${escape(value)}</div>
    </div>
  `;
}
function _center(label, value) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:20px;text-align:center;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 30px 'IBM Plex Mono',monospace;color:var(--a-ink);">${escape(value)}</div>
      <div style="font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);margin-top:7px;">${escape(label)}</div>
    </div>
  `;
}
function _kv(label, value) {
  return `
    <div>
      <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;color:var(--a-faint);">${escape(label)}</div>
      <div style="font:500 13px 'IBM Plex Mono',monospace;color:var(--a-ink-2);margin-top:6px;word-break:break-all;">${escape(value ?? '—')}</div>
    </div>
  `;
}
function _rankEmoji(rank) {
  if (rank === 1) return '①';
  if (rank === 2) return '②';
  if (rank === 3) return '③';
  return String(rank);
}

function _accountStatePanel(id) {
  const state = id.account_state || 'active';
  const notice = id.account_state_notice || '';
  const isAdmin = !!id.is_admin;

  const badge = state === 'banned'
    ? '<span style="padding:5px 10px;border-radius:999px;font:700 11px \'IBM Plex Sans\',sans-serif;letter-spacing:.05em;background:rgba(248,113,113,.16);color:#F87171;border:1px solid rgba(248,113,113,.4);">BANNED</span>'
    : state === 'under_investigation'
    ? '<span style="padding:5px 10px;border-radius:999px;font:700 11px \'IBM Plex Sans\',sans-serif;letter-spacing:.05em;background:rgba(251,191,36,.16);color:#FBBF24;border:1px solid rgba(251,191,36,.4);">UNDER INVESTIGATION</span>'
    : '<span style="padding:5px 10px;border-radius:999px;font:700 11px \'IBM Plex Sans\',sans-serif;letter-spacing:.05em;background:rgba(74,222,128,.14);color:#4ADE80;border:1px solid rgba(74,222,128,.35);">ACTIVE</span>';

  const disabledStyle = isAdmin ? 'opacity:.4;cursor:not-allowed;pointer-events:none;' : '';
  const disabledAttr = isAdmin ? 'disabled' : '';

  const buttons = [];
  if (state !== 'under_investigation') {
    buttons.push(`<button type="button" data-transition="under_investigation" ${disabledAttr}
      style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:rgba(251,191,36,.14);border:1px solid rgba(251,191,36,.4);color:#FBBF24;cursor:pointer;${disabledStyle}">
      Place under investigation
    </button>`);
  }
  if (state !== 'banned') {
    buttons.push(`<button type="button" data-transition="banned" ${disabledAttr}
      style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:rgba(248,113,113,.14);border:1px solid rgba(248,113,113,.4);color:#F87171;cursor:pointer;${disabledStyle}">
      Ban account
    </button>`);
  }
  if (state !== 'active') {
    const label = state === 'banned' ? 'Reinstate account' : 'Clear investigation';
    buttons.push(`<button type="button" data-transition="active"
      style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:rgba(74,222,128,.14);border:1px solid rgba(74,222,128,.4);color:#4ADE80;cursor:pointer;">
      ${label}
    </button>`);
  }

  const noticeBlock = state !== 'active' && notice
    ? `<div style="margin-top:12px;padding:12px 14px;background:var(--a-panel-2);border-left:3px solid var(--a-brand);border-radius:8px;font:400 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);line-height:1.55;white-space:pre-wrap;">${escape(notice)}</div>`
    : '';

  const selfNote = isAdmin
    ? '<div style="margin-top:10px;font:400 11px \'IBM Plex Sans\',sans-serif;color:var(--a-faint);">Admin accounts can\'t be restricted from this UI.</div>'
    : '';

  return `
    <div class="adm2-section-label">Account state</div>
    <div class="adm2-panel" id="adm2-account-state-panel" data-user-id="${escape(id.id)}" data-user-email="${escape(id.email)}" data-state="${escape(state)}">
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        ${badge}
        <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">
          Coarse penalty axis — orthogonal to the blacklist handicap.
        </span>
      </div>
      ${noticeBlock}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:14px;">
        ${buttons.join('')}
      </div>
      ${selfNote}
    </div>
  `;
}

// Bonus wallet powerup-only exemption + punitive cash → bonus split.
// Both surfaces call new admin endpoints (see backend admin.py):
//   POST /admin/users/{id}/bonus-powerup-only
//   POST /admin/users/{id}/wallet/split-to-bonus
function _bonusPowerupPanel(id, bal) {
  const on = !!id.bonus_powerup_only;
  const cashKobo = Number(bal && bal.wallet_kobo != null ? bal.wallet_kobo : 0);
  const badge = on
    ? '<span style="padding:5px 10px;border-radius:999px;font:700 11px \'IBM Plex Sans\',sans-serif;letter-spacing:.05em;background:rgba(139,107,242,.16);color:#B7A5F5;border:1px solid rgba(139,107,242,.4);">POWERUP-ONLY BONUS</span>'
    : '<span style="padding:5px 10px;border-radius:999px;font:700 11px \'IBM Plex Sans\',sans-serif;letter-spacing:.05em;background:rgba(148,163,184,.14);color:var(--a-muted);border:1px solid var(--a-border);">BONUS UNRESTRICTED</span>';

  const toggleLabel = on ? 'Lift powerup-only restriction' : 'Restrict bonus to powerups only';
  const toggleColor = on ? '#4ADE80' : '#B7A5F5';
  const toggleBg = on ? 'rgba(74,222,128,.14)' : 'rgba(139,107,242,.16)';
  const toggleBorder = on ? 'rgba(74,222,128,.4)' : 'rgba(139,107,242,.4)';

  const splitDisabled = cashKobo <= 0;
  const splitBtnStyle = splitDisabled ? 'opacity:.4;cursor:not-allowed;' : '';
  const splitBtnAttr = splitDisabled ? 'disabled' : '';

  return `
    <div class="adm2-section-label">Bonus wallet restriction</div>
    <div class="adm2-panel" id="adm2-bonus-restriction-panel"
         data-user-id="${escape(id.id)}"
         data-user-email="${escape(id.email)}"
         data-cash-kobo="${escape(String(cashKobo))}"
         data-on="${on ? '1' : '0'}">
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        ${badge}
        <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">
          When restricted, bonus balance can only buy power-ups. Paid duel stakes fall back to cash-only.
        </span>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:14px;">
        <button type="button" id="adm2-bonus-toggle"
          style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:${toggleBg};border:1px solid ${toggleBorder};color:${toggleColor};cursor:pointer;">
          ${toggleLabel}
        </button>
        <button type="button" id="adm2-bonus-split" ${splitBtnAttr}
          style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:rgba(180,83,9,.18);border:1px solid rgba(180,83,9,.5);color:#FBBF24;cursor:pointer;${splitBtnStyle}">
          Split cash → bonus…
        </button>
        <button type="button" id="adm2-bonus-clawback" ${splitBtnAttr}
          style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:rgba(220,38,38,.16);border:1px solid rgba(220,38,38,.5);color:#F87171;cursor:pointer;${splitBtnStyle}">
          Claw back cash…
        </button>
      </div>
      ${splitDisabled ? '<div style="margin-top:10px;font:400 11px \'IBM Plex Sans\',sans-serif;color:var(--a-faint);">Split and clawback disabled — user has no cash balance.</div>' : ''}
    </div>
  `;
}

function _wire(host, ctx, data) {
  const btn = host.querySelector('#adm2-user-blacklist');
  if (btn) {
    btn.addEventListener('click', async () => {
      const action = btn.dataset.action;
      const userId = btn.dataset.userId;
      const verb = action === 'add' ? 'Blacklist' : 'Unblacklist';
      if (!window.confirm(`${verb} ${data.identity.email}?`)) return;
      btn.disabled = true;
      try {
        await api(`/admin/users/${encodeURIComponent(userId)}/blacklist`, {
          method: action === 'add' ? 'POST' : 'DELETE',
        });
        toast.success(`${verb}ed ${data.identity.email}.`);
        await renderPlayerDetail(host, ctx);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          ctx.navigate('/login');
          return;
        }
        toast.fromError(err);
        btn.disabled = false;
      }
    });
  }

  const grantBtn = host.querySelector('#adm2-grant-powerup-btn');
  if (grantBtn) {
    grantBtn.addEventListener('click', () => {
      _openGrantPowerupModal({
        host,
        ctx,
        userId: grantBtn.dataset.userId,
        userEmail: grantBtn.dataset.userEmail,
      });
    });
  }

  const statePanel = host.querySelector('#adm2-account-state-panel');
  if (statePanel) {
    const userId = statePanel.dataset.userId;
    const userEmail = statePanel.dataset.userEmail;
    const currentState = statePanel.dataset.state;
    const cashKobo = Number((data.balances && data.balances.wallet_kobo) || 0);
    statePanel.querySelectorAll('button[data-transition]').forEach((btn) => {
      btn.addEventListener('click', () => {
        _openAccountStateModal({
          host,
          ctx,
          userId,
          userEmail,
          currentState,
          nextState: btn.dataset.transition,
          cashKobo,
        });
      });
    });
  }

  const bonusPanel = host.querySelector('#adm2-bonus-restriction-panel');
  if (bonusPanel) {
    const userId = bonusPanel.dataset.userId;
    const userEmail = bonusPanel.dataset.userEmail;
    const cashKobo = Number(bonusPanel.dataset.cashKobo || '0');
    const on = bonusPanel.dataset.on === '1';
    const toggle = bonusPanel.querySelector('#adm2-bonus-toggle');
    const split = bonusPanel.querySelector('#adm2-bonus-split');
    if (toggle) {
      toggle.addEventListener('click', () => {
        _openBonusToggleModal({ host, ctx, userId, userEmail, currentlyOn: on });
      });
    }
    if (split && !split.disabled) {
      split.addEventListener('click', () => {
        _openSplitToBonusModal({ host, ctx, userId, userEmail, cashKobo });
      });
    }
    const clawback = bonusPanel.querySelector('#adm2-bonus-clawback');
    if (clawback && !clawback.disabled) {
      clawback.addEventListener('click', () => {
        _openClawbackModal({ host, ctx, userId, userEmail, cashKobo });
      });
    }
  }

  _wireRetestPanel(host, ctx, data);
}


// ---------------------------------------------------------------------------
// Goodwill power-up grant modal
// ---------------------------------------------------------------------------
//
// Mounts on document.body — same reason as the suspicion drill-down modal
// (see admin_v2/live.js): the admin_v2 CSS variables are scoped to `.adm2`
// so a body-level card needs a hardcoded palette. Using the same dark
// palette here for consistency between admin surfaces.
const _MODAL_BG = '#141D30';
const _MODAL_BG_ALT = '#1B2438';
const _MODAL_BORDER = '#2A3650';
const _MODAL_INK = '#E9EEF7';
const _MODAL_MUTED = '#8595B2';
const _MODAL_FAINT = '#66758F';
const _MODAL_TRACK = '#222C42';
const _MODAL_BRAND = '#8B6BF2';

function _openGrantPowerupModal({ host, ctx, userId, userEmail }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') {
      close();
      document.removeEventListener('keydown', onKey);
    }
  });
  overlay.innerHTML = `
    <div style="max-width:460px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">
              Goodwill power-up grant
            </div>
            <div style="font-size:15px;color:${_MODAL_INK};font-weight:600;margin-top:4px;">
              ${escape(userEmail)}
            </div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
      </div>
      <form id="adm2-grant-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Power-up type</span>
          <select name="powerup_type" required
                  style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;">
            <option value="eraser">Eraser</option>
            <option value="fiftyfifty">50/50</option>
          </select>
        </label>
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Count</span>
          <input type="number" name="count" required min="1" max="50" value="1"
                 style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:'IBM Plex Mono',monospace;" />
        </label>
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Reason <span style="color:${_MODAL_MUTED};font-weight:500;text-transform:none;letter-spacing:0;">(audit note — required)</span></span>
          <textarea name="reason" required minlength="3" maxlength="200" rows="2"
                    placeholder="e.g. reported purchase confusion; make-good for missed live trivia"
                    style="padding:9px 10px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;resize:vertical;font-family:inherit;line-height:1.4;"></textarea>
        </label>
        <div id="adm2-grant-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">
            Cancel
          </button>
          <button type="submit" id="adm2-grant-submit"
                  style="padding:9px 18px;font-size:13px;font-weight:700;background:${_MODAL_BRAND};border:1px solid ${_MODAL_BRAND};color:#fff;border-radius:6px;cursor:pointer;">
            Grant
          </button>
        </div>
      </form>
      <div style="padding:12px 22px;background:${_MODAL_BG_ALT};border-top:1px solid ${_MODAL_BORDER};font-size:11px;color:${_MODAL_MUTED};line-height:1.5;">
        Writes a <span style="font-family:'IBM Plex Mono',monospace;color:${_MODAL_INK};">reason='admin'</span> row to <span style="font-family:'IBM Plex Mono',monospace;color:${_MODAL_INK};">powerup_ledger</span> so the audit trail distinguishes goodwill grants from purchases and rank rewards.
      </div>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

  overlay.querySelector('#adm2-grant-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const form = ev.currentTarget;
    const submit = form.querySelector('#adm2-grant-submit');
    const errBox = overlay.querySelector('#adm2-grant-error');
    const body = {
      powerup_type: form.powerup_type.value,
      count: Number(form.count.value),
      reason: form.reason.value.trim(),
    };
    errBox.style.display = 'none';
    submit.disabled = true;
    submit.textContent = 'Granting…';
    try {
      const res = await api(`/admin/users/${encodeURIComponent(userId)}/powerups/grant`, {
        method: 'POST',
        body,
      });
      const label = res.powerup_type === 'eraser' ? 'eraser' : '50/50';
      toast.success(
        `Granted ${res.granted_count} ${label}${res.granted_count === 1 ? '' : 's'} to ${userEmail}. New balance: ${res.new_balance}.`,
      );
      close();
      // Re-render the whole page so the inventory tile shows the new balance.
      await renderPlayerDetail(host, ctx);
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Grant failed.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = 'Grant';
    }
  });

  document.body.appendChild(overlay);
}


// ---------------------------------------------------------------------------
// Account state transition modal
//
// Same body-mount + hardcoded palette pattern as the goodwill grant modal
// (see note at _MODAL_BG). Fires POST /admin/users/{id}/account-state with
// the new state + admin_message + reason. The admin_message is what the
// user reads verbatim in the notice email + on their screen; reason is
// an internal audit note.
// ---------------------------------------------------------------------------

function _openAccountStateModal({ host, ctx, userId, userEmail, currentState, nextState, cashKobo }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') {
      close();
      document.removeEventListener('keydown', onKey);
    }
  });

  const meta = _stateTransitionMeta(currentState, nextState);
  const messageRequired = nextState !== 'active';
  const messagePlaceholder = messageRequired
    ? (nextState === 'banned'
        ? 'e.g. Repeated attempts to manipulate scoring across three live trivias; multiple accounts linked to same device.'
        : 'e.g. Reviewing a burst of eraser purchases from a new IP — please verify your recent activity.')
    : (currentState === 'banned'
        ? 'Optional note the user reads when the reinstatement email arrives.'
        : 'Optional note the user reads when the investigation-cleared email arrives.');

  // Punitive split-to-bonus is only offered on the investigation
  // transition, and only when the user has cash to move. Chips fire the
  // POST /wallet/split-to-bonus endpoint as a follow-up to the state
  // change; both writes are independent so a split failure after the
  // state change lands still leaves the state applied (with a toast).
  const showSplitSection = nextState === 'under_investigation' && Number(cashKobo || 0) > 0;
  const splitSectionHtml = showSplitSection ? `
    <div style="margin-top:6px;padding:12px 14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:8px;">
      <div style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Optional · punitive split</div>
      <div style="margin-top:6px;font-size:12.5px;color:${_MODAL_MUTED};line-height:1.5;">
        Move a percentage of cash → bonus and flip powerup-only ON. Rounds down.
      </div>
      <div style="margin-top:8px;font:500 12px 'IBM Plex Mono',monospace;color:${_MODAL_MUTED};">Current cash: <span style="color:${_MODAL_INK};">${escape(_fmtKobo(cashKobo))}</span></div>
      <div id="adm2-tx-split-chips" style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px;">
        ${[0, 25, 50, 75, 100].map((p) => `
          <button type="button" data-tx-split="${p}"
            style="padding:6px 10px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:6px;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};cursor:pointer;">
            ${p === 0 ? 'Skip' : p + '%'}
          </button>
        `).join('')}
      </div>
      <div id="adm2-tx-split-preview" style="margin-top:8px;font:500 11.5px 'IBM Plex Mono',monospace;color:${_MODAL_FAINT};min-height:14px;"></div>
    </div>
  ` : '';

  // Optional clawback section — same shape as the split section, but
  // routes to platform_revenue instead of bonus. Independent chip
  // picker: admin can split AND claw back on the same submit if the
  // punishment warrants both. Applied against the CURRENT cash balance
  // at the moment of submit, so if a split runs first the clawback
  // percentage effectively bites into the reduced balance.
  const clawbackSectionHtml = showSplitSection ? `
    <div style="margin-top:6px;padding:12px 14px;background:rgba(220,38,38,.08);border:1px solid rgba(220,38,38,.3);border-radius:8px;">
      <div style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:#F87171;font-weight:700;">Optional · cash clawback</div>
      <div style="margin-top:6px;font-size:12.5px;color:${_MODAL_MUTED};line-height:1.5;">
        Move a percentage of cash → platform_revenue. Destructive; not undone by reinstate. Rounds down.
      </div>
      <div id="adm2-tx-clawback-chips" style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px;">
        ${[0, 25, 50, 75, 100].map((p) => `
          <button type="button" data-tx-clawback="${p}"
            style="padding:6px 10px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:6px;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};cursor:pointer;">
            ${p === 0 ? 'Skip' : p + '%'}
          </button>
        `).join('')}
      </div>
      <div id="adm2-tx-clawback-preview" style="margin-top:8px;font:500 11.5px 'IBM Plex Mono',monospace;color:${_MODAL_FAINT};min-height:14px;"></div>
    </div>
  ` : '';

  overlay.innerHTML = `
    <div style="max-width:520px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">
              ${escape(meta.chip)}
            </div>
            <div style="font-size:16px;color:${_MODAL_INK};font-weight:700;margin-top:6px;">
              ${escape(meta.title)}
            </div>
            <div style="font-size:13px;color:${_MODAL_MUTED};margin-top:4px;">
              ${escape(userEmail)}
            </div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
        <div style="margin-top:12px;padding:10px 12px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:8px;font-size:12.5px;color:${_MODAL_MUTED};line-height:1.55;">
          ${escape(meta.blurb)}
        </div>
      </div>
      <form id="adm2-state-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">
            Message to the user ${messageRequired ? '<span style="color:#F87171;">*</span>' : '<span style="color:'+_MODAL_MUTED+';font-weight:500;text-transform:none;letter-spacing:0;">(optional)</span>'}
          </span>
          <textarea name="admin_message" rows="4" ${messageRequired ? 'required minlength="3"' : ''} maxlength="2000"
                    placeholder="${escape(messagePlaceholder)}"
                    style="padding:10px 12px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;resize:vertical;font-family:inherit;line-height:1.5;"></textarea>
          <span style="font-size:11px;color:${_MODAL_FAINT};line-height:1.5;">
            This is what the user reads in the notice email and on their screen.
          </span>
        </label>
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">
            Internal reason <span style="color:#F87171;">*</span>
            <span style="color:${_MODAL_MUTED};font-weight:500;text-transform:none;letter-spacing:0;"> (audit note — never shown to user)</span>
          </span>
          <input type="text" name="reason" required minlength="3" maxlength="200"
                 placeholder="e.g. reported by 3 users; matching device sig with banned account X"
                 style="padding:9px 10px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;" />
        </label>
        ${splitSectionHtml}
        ${clawbackSectionHtml}
        <div id="adm2-state-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">
            Cancel
          </button>
          <button type="submit" id="adm2-state-submit"
                  style="padding:9px 20px;font-size:13px;font-weight:700;background:${meta.submitBg};border:1px solid ${meta.submitBg};color:#fff;border-radius:6px;cursor:pointer;">
            ${escape(meta.submit)}
          </button>
        </div>
      </form>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

  // Chip picker for the optional punitive split. `Skip` (0%) leaves
  // the split unset; any other value triggers a follow-up POST after
  // the state transition succeeds.
  let selectedSplitPercent = 0;
  const splitChips = overlay.querySelectorAll('button[data-tx-split]');
  const splitPreview = overlay.querySelector('#adm2-tx-split-preview');
  if (splitChips.length) {
    splitChips.forEach((btn) => {
      btn.addEventListener('click', () => {
        selectedSplitPercent = Number(btn.dataset.txSplit);
        splitChips.forEach((b) => {
          const on = Number(b.dataset.txSplit) === selectedSplitPercent;
          b.style.background = on ? _MODAL_BRAND : _MODAL_BG;
          b.style.borderColor = on ? _MODAL_BRAND : _MODAL_BORDER;
          b.style.color = on ? '#fff' : _MODAL_INK;
        });
        if (selectedSplitPercent === 0) {
          splitPreview.textContent = 'No split — cash + bonus wallets untouched.';
        } else {
          const moved = Math.floor((Number(cashKobo || 0) * selectedSplitPercent) / 100);
          splitPreview.innerHTML = `Will move <span style="color:${_MODAL_INK};">${escape(_fmtKobo(moved))}</span> → bonus and flip powerup-only ON.`;
        }
      });
    });
  }

  // Independent chip picker for the optional clawback. Same 0-skip
  // convention; fires a separate POST after the state change lands.
  // Preview reads the CURRENT cashKobo when clicked; if the admin
  // also picks a split, the actual clawback at submit time will apply
  // to the reduced balance server-side (each endpoint reads live).
  let selectedClawbackPercent = 0;
  const clawbackChips = overlay.querySelectorAll('button[data-tx-clawback]');
  const clawbackPreview = overlay.querySelector('#adm2-tx-clawback-preview');
  if (clawbackChips.length) {
    clawbackChips.forEach((btn) => {
      btn.addEventListener('click', () => {
        selectedClawbackPercent = Number(btn.dataset.txClawback);
        clawbackChips.forEach((b) => {
          const on = Number(b.dataset.txClawback) === selectedClawbackPercent;
          b.style.background = on ? '#B91C1C' : _MODAL_BG;
          b.style.borderColor = on ? '#B91C1C' : _MODAL_BORDER;
          b.style.color = on ? '#fff' : _MODAL_INK;
        });
        if (selectedClawbackPercent === 0) {
          clawbackPreview.textContent = 'No clawback — cash wallet untouched.';
        } else {
          const moved = Math.floor((Number(cashKobo || 0) * selectedClawbackPercent) / 100);
          clawbackPreview.innerHTML = `Will claw back roughly <span style="color:${_MODAL_INK};">${escape(_fmtKobo(moved))}</span> → platform_revenue (before any split reduces cash).`;
        }
      });
    });
  }

  overlay.querySelector('#adm2-state-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const form = ev.currentTarget;
    const submit = form.querySelector('#adm2-state-submit');
    const errBox = overlay.querySelector('#adm2-state-error');
    const body = {
      state: nextState,
      admin_message: form.admin_message.value,
      reason: form.reason.value.trim(),
    };
    errBox.style.display = 'none';
    submit.disabled = true;
    submit.textContent = 'Applying…';
    try {
      await api(`/admin/users/${encodeURIComponent(userId)}/account-state`, {
        method: 'POST',
        body,
      });
      // Optional follow-ups — only offered on the investigation
      // transition, only for chip values > 0. State change has
      // already landed, so failures here surface as separate toasts
      // and the state remains applied.
      //
      // Order: split first, then clawback. The clawback endpoint reads
      // the wallet balance live so if the admin picked both, the
      // clawback percentage bites into the post-split cash.
      let followSummary = '';
      if (nextState === 'under_investigation' && selectedSplitPercent > 0) {
        try {
          const res = await api(`/admin/users/${encodeURIComponent(userId)}/wallet/split-to-bonus`, {
            method: 'POST',
            body: {
              percent: selectedSplitPercent,
              reason: form.reason.value.trim(),
            },
          });
          followSummary += ` Moved ${_fmtKobo(res.moved_kobo)} → bonus.`;
        } catch (splitErr) {
          toast.error((splitErr && splitErr.message) || 'State updated but split failed.');
        }
      }
      if (nextState === 'under_investigation' && selectedClawbackPercent > 0) {
        try {
          const res = await api(`/admin/users/${encodeURIComponent(userId)}/wallet/clawback`, {
            method: 'POST',
            body: {
              percent: selectedClawbackPercent,
              reason: form.reason.value.trim(),
            },
          });
          followSummary += ` Clawed back ${_fmtKobo(res.clawed_kobo)} → platform.`;
        } catch (clawbackErr) {
          toast.error((clawbackErr && clawbackErr.message) || 'State updated but clawback failed.');
        }
      }
      toast.success(`${meta.successToast}${followSummary}`);
      close();
      await renderPlayerDetail(host, ctx);
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Failed to update account state.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = meta.submit;
    }
  });

  document.body.appendChild(overlay);
}

function _stateTransitionMeta(currentState, nextState) {
  if (nextState === 'under_investigation') {
    return {
      chip: 'Account state · restrict',
      title: 'Place under investigation',
      blurb: 'The user can still log in and play solo practice + free 1-on-1 duels. Deposits, withdrawals, paid trivias, power-up purchases, paid duels, and profile edits are blocked while the review is open. A notice email is sent.',
      submit: 'Place under investigation',
      submitBg: '#B45309',
      successToast: 'User placed under investigation. Notice email sent.',
    };
  }
  if (nextState === 'banned') {
    return {
      chip: 'Account state · hard block',
      title: 'Ban this account',
      blurb: 'The user is logged out on their next refresh and blocked at the login screen. A ban email is sent carrying the message below.',
      submit: 'Ban account',
      submitBg: '#B91C1C',
      successToast: 'Account banned. Notice email sent.',
    };
  }
  // active
  if (currentState === 'banned') {
    return {
      chip: 'Account state · unblock',
      title: 'Reinstate account',
      blurb: 'The user can log in again. A reinstatement email is sent.',
      submit: 'Reinstate account',
      submitBg: '#047857',
      successToast: 'Account reinstated. Notice email sent.',
    };
  }
  return {
    chip: 'Account state · restore',
    title: 'Clear investigation',
    blurb: 'All money movement + paid stakes + profile edits are unlocked again. An "unlocked" notice email is sent.',
    submit: 'Clear investigation',
    submitBg: '#047857',
    successToast: 'Investigation cleared. Notice email sent.',
  };
}


// ---------------------------------------------------------------------------
// Bonus wallet: powerup-only toggle modal
// ---------------------------------------------------------------------------

function _openBonusToggleModal({ host, ctx, userId, userEmail, currentlyOn }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  });

  const nextEnabled = !currentlyOn;
  const title = nextEnabled ? 'Restrict bonus wallet to power-ups' : 'Lift bonus wallet restriction';
  const blurb = nextEnabled
    ? "The user's bonus balance can only be spent on eraser + 50/50 purchases. Paid duel stakes fall back to cash-only; if cash is insufficient the join fails. Existing bonus balance is untouched."
    : "The user's bonus balance can be staked in paid duels again. Duel winnings still pay to cash, so this reopens the sanctioned bonus → cash conversion path (15% rake).";
  const submitLabel = nextEnabled ? 'Restrict' : 'Lift restriction';
  const submitBg = nextEnabled ? _MODAL_BRAND : '#047857';

  overlay.innerHTML = `
    <div style="max-width:480px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Bonus wallet · restriction</div>
            <div style="font-size:16px;color:${_MODAL_INK};font-weight:700;margin-top:6px;">${escape(title)}</div>
            <div style="font-size:13px;color:${_MODAL_MUTED};margin-top:4px;">${escape(userEmail)}</div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
        <div style="margin-top:12px;padding:10px 12px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:8px;font-size:12.5px;color:${_MODAL_MUTED};line-height:1.55;">${escape(blurb)}</div>
      </div>
      <form id="adm2-bonus-toggle-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Internal reason <span style="color:#F87171;">*</span> <span style="color:${_MODAL_MUTED};font-weight:500;text-transform:none;letter-spacing:0;">(audit note — never shown to user)</span></span>
          <input type="text" name="reason" required minlength="3" maxlength="200"
                 placeholder="e.g. under-investigation punishment; suspected chargeback abuse"
                 style="padding:9px 10px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;" />
        </label>
        <div id="adm2-bonus-toggle-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">Cancel</button>
          <button type="submit" id="adm2-bonus-toggle-submit"
                  style="padding:9px 20px;font-size:13px;font-weight:700;background:${submitBg};border:1px solid ${submitBg};color:#fff;border-radius:6px;cursor:pointer;">${escape(submitLabel)}</button>
        </div>
      </form>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));
  overlay.querySelector('#adm2-bonus-toggle-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const form = ev.currentTarget;
    const submit = form.querySelector('#adm2-bonus-toggle-submit');
    const errBox = overlay.querySelector('#adm2-bonus-toggle-error');
    errBox.style.display = 'none';
    submit.disabled = true;
    submit.textContent = 'Applying…';
    try {
      await api(`/admin/users/${encodeURIComponent(userId)}/bonus-powerup-only`, {
        method: 'POST',
        body: { enabled: nextEnabled, reason: form.reason.value.trim() },
      });
      toast.success(nextEnabled ? 'Bonus wallet restricted to power-ups.' : 'Bonus wallet restriction lifted.');
      close();
      await renderPlayerDetail(host, ctx);
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Failed.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = submitLabel;
    }
  });
  document.body.appendChild(overlay);
}


// ---------------------------------------------------------------------------
// Bonus wallet: split cash → bonus modal
// ---------------------------------------------------------------------------
//
// Preset chips at 25 / 50 / 75 / 100%. Also flips bonus_powerup_only=true
// on the user (server-side, unconditional) so the moved kobo can only
// buy power-ups. Cash balance is passed in so the modal can preview the
// resulting move.

function _fmtKobo(k) {
  return `₦${(Number(k || 0) / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function _openSplitToBonusModal({ host, ctx, userId, userEmail, cashKobo }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  });

  const chips = [25, 50, 75, 100].map((p) => `
    <button type="button" data-percent="${p}"
      style="padding:9px 14px;font:600 13px 'IBM Plex Sans',sans-serif;border-radius:8px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};cursor:pointer;flex:1;min-width:70px;">
      ${p}%
    </button>
  `).join('');

  overlay.innerHTML = `
    <div style="max-width:520px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Bonus wallet · punitive split</div>
            <div style="font-size:16px;color:${_MODAL_INK};font-weight:700;margin-top:6px;">Move cash → bonus</div>
            <div style="font-size:13px;color:${_MODAL_MUTED};margin-top:4px;">${escape(userEmail)}</div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
        <div style="margin-top:12px;padding:10px 12px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:8px;font-size:12.5px;color:${_MODAL_MUTED};line-height:1.55;">
          Moves a percentage of the user's current cash balance to their bonus wallet. Also flips the powerup-only restriction ON so the moved kobo can only buy power-ups. Rounds down on integer remainders.
        </div>
        <div style="margin-top:10px;font:500 12px 'IBM Plex Mono',monospace;color:${_MODAL_MUTED};">Current cash balance: <span style="color:${_MODAL_INK};">${escape(_fmtKobo(cashKobo))}</span></div>
      </div>
      <form id="adm2-split-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <div>
          <div style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;margin-bottom:8px;">Percent to move</div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">${chips}</div>
          <div id="adm2-split-preview" style="margin-top:10px;font:500 12px 'IBM Plex Mono',monospace;color:${_MODAL_FAINT};">Select a percent to preview the move.</div>
        </div>
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Internal reason <span style="color:#F87171;">*</span></span>
          <input type="text" name="reason" required minlength="3" maxlength="200"
                 placeholder="e.g. under-investigation punishment split"
                 style="padding:9px 10px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;" />
        </label>
        <div id="adm2-split-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">Cancel</button>
          <button type="submit" id="adm2-split-submit" disabled
                  style="padding:9px 20px;font-size:13px;font-weight:700;background:#B45309;border:1px solid #B45309;color:#fff;border-radius:6px;cursor:pointer;opacity:.5;">Split</button>
        </div>
      </form>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

  let selectedPercent = null;
  const preview = overlay.querySelector('#adm2-split-preview');
  const submit = overlay.querySelector('#adm2-split-submit');
  overlay.querySelectorAll('button[data-percent]').forEach((btn) => {
    btn.addEventListener('click', () => {
      selectedPercent = Number(btn.dataset.percent);
      overlay.querySelectorAll('button[data-percent]').forEach((b) => {
        const on = Number(b.dataset.percent) === selectedPercent;
        b.style.background = on ? _MODAL_BRAND : _MODAL_BG_ALT;
        b.style.borderColor = on ? _MODAL_BRAND : _MODAL_BORDER;
        b.style.color = on ? '#fff' : _MODAL_INK;
      });
      const moved = Math.floor((cashKobo * selectedPercent) / 100);
      preview.innerHTML = `Will move <span style="color:${_MODAL_INK};">${escape(_fmtKobo(moved))}</span> → bonus wallet (${selectedPercent}% of cash) and flip powerup-only ON.`;
      submit.disabled = false;
      submit.style.opacity = '1';
    });
  });

  overlay.querySelector('#adm2-split-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    if (selectedPercent == null) return;
    const form = ev.currentTarget;
    const errBox = overlay.querySelector('#adm2-split-error');
    errBox.style.display = 'none';
    submit.disabled = true;
    submit.textContent = 'Splitting…';
    try {
      const res = await api(`/admin/users/${encodeURIComponent(userId)}/wallet/split-to-bonus`, {
        method: 'POST',
        body: { percent: selectedPercent, reason: form.reason.value.trim() },
      });
      toast.success(`Moved ${_fmtKobo(res.moved_kobo)} to bonus wallet. Powerup-only restriction ON.`);
      close();
      await renderPlayerDetail(host, ctx);
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Split failed.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = 'Split';
    }
  });

  document.body.appendChild(overlay);
}


// ---------------------------------------------------------------------------
// Bonus wallet: cash clawback modal
// ---------------------------------------------------------------------------
//
// Symmetric to _openSplitToBonusModal but destructive — moves cash to
// platform_revenue instead of the user's bonus wallet. Red palette to
// signal that this cannot be undone by a UI action; requires a
// goodwill credit to unwind.

function _openClawbackModal({ host, ctx, userId, userEmail, cashKobo }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  });

  const chips = [25, 50, 75, 100].map((p) => `
    <button type="button" data-percent="${p}"
      style="padding:9px 14px;font:600 13px 'IBM Plex Sans',sans-serif;border-radius:8px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};cursor:pointer;flex:1;min-width:70px;">
      ${p}%
    </button>
  `).join('');

  overlay.innerHTML = `
    <div style="max-width:520px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#F87171;font-weight:700;">Cash clawback · destructive</div>
            <div style="font-size:16px;color:${_MODAL_INK};font-weight:700;margin-top:6px;">Claw back cash to platform</div>
            <div style="font-size:13px;color:${_MODAL_MUTED};margin-top:4px;">${escape(userEmail)}</div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
        <div style="margin-top:12px;padding:10px 12px;background:rgba(220,38,38,.10);border:1px solid rgba(220,38,38,.35);border-radius:8px;font-size:12.5px;color:#FCA5A5;line-height:1.55;">
          Moves a percentage of the user's current cash balance to platform_revenue. The money is gone from the user — cannot be undone from this UI. Reversible only by a goodwill wallet credit if the review clears them.
        </div>
        <div style="margin-top:10px;font:500 12px 'IBM Plex Mono',monospace;color:${_MODAL_MUTED};">Current cash balance: <span style="color:${_MODAL_INK};">${escape(_fmtKobo(cashKobo))}</span></div>
      </div>
      <form id="adm2-clawback-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <div>
          <div style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;margin-bottom:8px;">Percent to claw back</div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">${chips}</div>
          <div id="adm2-clawback-preview" style="margin-top:10px;font:500 12px 'IBM Plex Mono',monospace;color:${_MODAL_FAINT};">Select a percent to preview the move.</div>
        </div>
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Internal reason <span style="color:#F87171;">*</span></span>
          <input type="text" name="reason" required minlength="3" maxlength="200"
                 placeholder="e.g. clawback of proceeds from confirmed multi-account abuse"
                 style="padding:9px 10px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;" />
        </label>
        <div id="adm2-clawback-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">Cancel</button>
          <button type="submit" id="adm2-clawback-submit" disabled
                  style="padding:9px 20px;font-size:13px;font-weight:700;background:#B91C1C;border:1px solid #B91C1C;color:#fff;border-radius:6px;cursor:pointer;opacity:.5;">Claw back</button>
        </div>
      </form>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

  let selectedPercent = null;
  const preview = overlay.querySelector('#adm2-clawback-preview');
  const submit = overlay.querySelector('#adm2-clawback-submit');
  overlay.querySelectorAll('button[data-percent]').forEach((btn) => {
    btn.addEventListener('click', () => {
      selectedPercent = Number(btn.dataset.percent);
      overlay.querySelectorAll('button[data-percent]').forEach((b) => {
        const on = Number(b.dataset.percent) === selectedPercent;
        b.style.background = on ? '#B91C1C' : _MODAL_BG_ALT;
        b.style.borderColor = on ? '#B91C1C' : _MODAL_BORDER;
        b.style.color = on ? '#fff' : _MODAL_INK;
      });
      const moved = Math.floor((cashKobo * selectedPercent) / 100);
      preview.innerHTML = `Will remove <span style="color:${_MODAL_INK};">${escape(_fmtKobo(moved))}</span> from cash wallet → platform_revenue (${selectedPercent}% of cash).`;
      submit.disabled = false;
      submit.style.opacity = '1';
    });
  });

  overlay.querySelector('#adm2-clawback-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    if (selectedPercent == null) return;
    const form = ev.currentTarget;
    const errBox = overlay.querySelector('#adm2-clawback-error');
    errBox.style.display = 'none';
    submit.disabled = true;
    submit.textContent = 'Clawing back…';
    try {
      const res = await api(`/admin/users/${encodeURIComponent(userId)}/wallet/clawback`, {
        method: 'POST',
        body: { percent: selectedPercent, reason: form.reason.value.trim() },
      });
      toast.success(`Clawed back ${_fmtKobo(res.clawed_kobo)} to platform_revenue.`);
      close();
      await renderPlayerDetail(host, ctx);
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Clawback failed.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = 'Claw back';
    }
  });

  document.body.appendChild(overlay);
}


// ---------------------------------------------------------------------------
// Re-test quiz — "defend your score" adjudication surface.
//
// Advisory surface only. Nothing here writes account_state; the review
// endpoint's response says so explicitly. When an admin records a
// verdict we redirect their attention to the account-state control
// above with a highlighted pointer and the user's CURRENT state.
//
// Panel structure:
//   * Header + "Send re-test" button (enabled iff the target user is
//     `under_investigation` — the backend rejects otherwise; we hide
//     the button to make that obvious).
//   * A list of the user's recent re-tests (fetched lazily on first
//     panel render). Each row shows status, deadline (in WAT),
//     comparison band + accuracy pair for submitted rows, and:
//       - "Extend" for active rows (sent/in_progress).
//       - "Review" for terminal rows without a verdict yet.
//       - "View" for reviewed rows.
// ---------------------------------------------------------------------------

function _retestPanel(id, d) {
  const state = id.account_state || 'active';
  const eligible = state === 'under_investigation';
  const disabledStyle = eligible
    ? ''
    : 'opacity:.45;cursor:not-allowed;';
  const disabledAttr = eligible ? '' : 'disabled';
  // Source picker fills from every live trivia the user JOINED — not
  // just podium finishes. A borderline result usually shows up mid-
  // pack or with no placement at all, so honours alone were too
  // narrow (fixed post-PR1). Duels aren't exposed on the
  // admin/metrics/user endpoint yet — an admin needing to defend a
  // duel can still enter the id manually in the modal.
  const participations = (d.live && Array.isArray(d.live.participations))
    ? d.live.participations
    : [];
  // Fall back to honours for older backend versions that haven't been
  // rolled with the participations field yet — keeps the picker
  // populated during a rolling deploy.
  const legacyHonours = (d.live && Array.isArray(d.live.honours)) ? d.live.honours : [];
  const sourceCandidates = participations.length ? participations : legacyHonours;
  const sourcesJson = escape(JSON.stringify(sourceCandidates.slice(0, 30)));

  return `
    <div class="adm2-section-label">Adjudication re-test</div>
    <div class="adm2-panel"
         id="adm2-retest-panel"
         data-user-id="${escape(id.id)}"
         data-user-email="${escape(id.email)}"
         data-user-state="${escape(state)}"
         data-sources='${sourcesJson}'>
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        <span style="padding:5px 10px;border-radius:999px;font:700 11px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;background:rgba(139,107,242,.16);color:#B7A5F5;border:1px solid rgba(139,107,242,.4);">DEFEND YOUR SCORE</span>
        <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">
          Fresh-category quiz. Advisory only — recording a verdict does NOT change account state.
        </span>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:14px;">
        <button type="button" id="adm2-retest-send-btn" ${disabledAttr}
          style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:rgba(139,107,242,.16);border:1px solid rgba(139,107,242,.4);color:#B7A5F5;cursor:pointer;${disabledStyle}">
          Send re-test
        </button>
        <button type="button" id="adm2-retest-refresh-btn"
          style="padding:8px 14px;font:600 12px 'IBM Plex Sans',sans-serif;border-radius:8px;background:transparent;border:1px solid var(--a-border);color:var(--a-muted);cursor:pointer;">
          Refresh list
        </button>
      </div>
      ${!eligible
        ? `<div style="margin-top:10px;font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">
             Re-tests can only be issued to users under investigation.
             Move to that state first via the control above.
           </div>`
        : ''}
      <div id="adm2-retest-list" style="margin-top:14px;">
        <div class="muted" style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">Loading re-tests…</div>
      </div>
    </div>
  `;
}

async function _wireRetestPanel(host, ctx, data) {
  const panel = host.querySelector('#adm2-retest-panel');
  if (!panel) return;
  const userId = panel.dataset.userId;
  const userEmail = panel.dataset.userEmail;
  const userState = panel.dataset.userState;
  let sources = [];
  try { sources = JSON.parse(panel.dataset.sources || '[]'); } catch { sources = []; }

  const sendBtn = panel.querySelector('#adm2-retest-send-btn');
  if (sendBtn && !sendBtn.disabled) {
    sendBtn.addEventListener('click', () => {
      _openSendRetestModal({ host, ctx, userId, userEmail, sources });
    });
  }
  const refreshBtn = panel.querySelector('#adm2-retest-refresh-btn');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => _loadRetestList(panel, { host, ctx, userId }));
  }

  await _loadRetestList(panel, { host, ctx, userId });

  // Silence linters — the sources / userState locals are referenced by
  // the modal callbacks that close over them.
  void userState;
}

async function _loadRetestList(panel, { host, ctx, userId }) {
  const target = panel.querySelector('#adm2-retest-list');
  if (!target) return;
  target.innerHTML = `<div class="muted" style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">Loading re-tests…</div>`;
  let items = [];
  try {
    const res = await adminListRetests({ limit: 50 });
    items = (res && Array.isArray(res.items)) ? res.items : [];
  } catch (err) {
    target.innerHTML = `<div style="color:var(--a-danger);font:400 12px 'IBM Plex Sans',sans-serif;">Failed to load: ${escape(err.message || 'unknown error')}</div>`;
    return;
  }
  // Client-side filter: only show this user's rows. The list endpoint
  // could accept a filter param later; for now the volume is low.
  const rows = items.filter((r) => r.user_id === userId);
  if (rows.length === 0) {
    target.innerHTML = `<div class="muted" style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">No re-tests for this user yet.</div>`;
    return;
  }
  target.innerHTML = rows.map(_retestRowHtml).join('');
  target.querySelectorAll('button[data-retest-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const retestId = btn.dataset.retestId;
      const action = btn.dataset.retestAction;
      if (action === 'extend') {
        _openExtendRetestModal({ host, ctx, retestId, onDone: () => _loadRetestList(panel, { host, ctx, userId }) });
      } else if (action === 'view' || action === 'review') {
        _openRetestDetailModal({ host, ctx, retestId, onDone: () => { _loadRetestList(panel, { host, ctx, userId }); return renderPlayerDetail(host, ctx); } });
      }
    });
  });
}

function _retestRowHtml(r) {
  const statusColor = _retestStatusStyle(r.status);
  const source = r.source_kind === 'live' ? `live ${(r.source_id || '').slice(0, 8)}` : `duel ${(r.source_id || '').slice(0, 8)}`;
  const bandChip = r.comparison_band
    ? _retestBandChip(r.comparison_band)
    : '';
  const verdictChip = r.review_outcome
    ? `<span style="padding:3px 8px;border-radius:999px;font:700 10px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;background:rgba(139,107,242,.14);color:#B7A5F5;border:1px solid rgba(139,107,242,.4);">${escape(r.review_outcome)}</span>`
    : '';
  const acc = (r.original_accuracy != null && r.retest_accuracy != null)
    ? `<span style="font:600 12px 'IBM Plex Mono',monospace;color:var(--a-ink);">${(r.original_accuracy * 100).toFixed(0)}% → ${(r.retest_accuracy * 100).toFixed(0)}%</span>`
    : '';
  const canExtend = r.status === 'sent' || r.status === 'in_progress';
  const canReview = (r.status === 'submitted' || r.status === 'expired') && !r.review_outcome;
  const actions = [
    canExtend
      ? `<button type="button" data-retest-action="extend" data-retest-id="${escape(r.id)}"
          style="padding:5px 10px;font:600 11px 'IBM Plex Sans',sans-serif;border-radius:6px;background:rgba(251,191,36,.12);border:1px solid rgba(251,191,36,.4);color:#FBBF24;cursor:pointer;">Extend</button>`
      : '',
    canReview
      ? `<button type="button" data-retest-action="review" data-retest-id="${escape(r.id)}"
          style="padding:5px 10px;font:600 11px 'IBM Plex Sans',sans-serif;border-radius:6px;background:rgba(139,107,242,.16);border:1px solid rgba(139,107,242,.4);color:#B7A5F5;cursor:pointer;">Review</button>`
      : `<button type="button" data-retest-action="view" data-retest-id="${escape(r.id)}"
          style="padding:5px 10px;font:600 11px 'IBM Plex Sans',sans-serif;border-radius:6px;background:transparent;border:1px solid var(--a-border);color:var(--a-muted);cursor:pointer;">View</button>`,
  ].filter(Boolean).join('');
  return `
    <div style="display:flex;align-items:center;gap:12px;padding:11px 13px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;margin-bottom:8px;flex-wrap:wrap;">
      <span style="padding:3px 8px;border-radius:999px;font:700 10px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;${statusColor}">${escape(r.status)}</span>
      ${bandChip}
      ${verdictChip}
      <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">${escape(source)}</span>
      <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">deadline ${escape(formatLagosDeadline(r.deadline_at))}</span>
      ${acc}
      <div style="margin-left:auto;display:flex;gap:6px;">${actions}</div>
    </div>
  `;
}

function _retestStatusStyle(status) {
  switch (status) {
    case 'sent':
    case 'in_progress':
      return 'background:rgba(251,191,36,.14);color:#FBBF24;border:1px solid rgba(251,191,36,.4);';
    case 'submitted':
      return 'background:rgba(74,222,128,.14);color:#4ADE80;border:1px solid rgba(74,222,128,.4);';
    case 'expired':
      return 'background:rgba(248,113,113,.14);color:#F87171;border:1px solid rgba(248,113,113,.4);';
    default:
      return 'background:rgba(148,163,184,.14);color:var(--a-muted);border:1px solid var(--a-border);';
  }
}

function _retestBandChip(band) {
  const styles = {
    consistent: 'background:rgba(74,222,128,.12);color:#4ADE80;border:1px solid rgba(74,222,128,.35);',
    moderate_drop: 'background:rgba(251,191,36,.12);color:#FBBF24;border:1px solid rgba(251,191,36,.35);',
    severe_drop: 'background:rgba(248,113,113,.12);color:#F87171;border:1px solid rgba(248,113,113,.35);',
  };
  const style = styles[band] || 'background:transparent;color:var(--a-muted);border:1px solid var(--a-border);';
  return `<span style="padding:3px 8px;border-radius:999px;font:700 10px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;${style}">${escape(band.replace(/_/g, ' '))}</span>`;
}

// ---------------------------------------------------------------------------
// Send re-test modal
// ---------------------------------------------------------------------------

function _openSendRetestModal({ host, ctx, userId, userEmail, sources }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  });

  // Source label copes with EITHER shape:
  //   - new: `participations` row → has `live_status`, may or may not
  //     carry a `rank` (unplaced games are legal); may carry
  //     `finished_at`.
  //   - old: `honours` row → always has a `rank` and prize_kobo.
  // Rank is shown when present ("rank 7"), otherwise the trivia's
  // status ("finished" / "cancelled" / "live") so an admin can tell
  // "unranked but still played" from "user was DQ'd".
  const sourceOptions = (sources || []).map((h) => {
    const rankBit = (h.rank != null) ? `rank ${h.rank}` : (h.live_status || 'played');
    const label = `${h.title || 'Live trivia'} · ${rankBit} · ${(h.live_id || '').slice(0, 8)}`;
    return `<option value="live:${escape(h.live_id)}">${escape(label)}</option>`;
  }).join('');
  const preset24 = isoAtHoursFromNow(24);
  const preset48 = isoAtHoursFromNow(48);
  const preset72 = isoAtHoursFromNow(72);

  overlay.innerHTML = `
    <div style="max-width:520px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Send re-test</div>
            <div style="font-size:15px;color:${_MODAL_INK};font-weight:600;margin-top:4px;">${escape(userEmail)}</div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
      </div>
      <form id="adm2-retest-send-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Flagged game</span>
          ${sourceOptions
            ? `<select name="source_pick"
                     style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;">
                 ${sourceOptions}
                 <option value="custom">Custom (enter manually…)</option>
               </select>`
            : `<div style="font-size:12.5px;color:${_MODAL_MUTED};">This user hasn't joined any live trivia yet — enter a source manually below.</div>
               <input type="hidden" name="source_pick" value="custom" />`
          }
        </label>
        <div id="adm2-retest-custom-source" style="display:${sourceOptions ? 'none' : ''};flex-direction:column;gap:8px;padding:12px 14px;background:${_MODAL_BG_ALT};border:1px dashed ${_MODAL_BORDER};border-radius:8px;">
          <label style="display:flex;flex-direction:column;gap:6px;">
            <span style="font-size:11px;color:${_MODAL_FAINT};font-weight:700;text-transform:uppercase;letter-spacing:0.06em;">Source kind</span>
            <select name="custom_kind" style="padding:8px 10px;font-size:13px;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;">
              <option value="live">live</option>
              <option value="duel">duel</option>
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:6px;">
            <span style="font-size:11px;color:${_MODAL_FAINT};font-weight:700;text-transform:uppercase;letter-spacing:0.06em;">Source id</span>
            <input type="text" name="custom_id" placeholder="uuid…" style="padding:8px 10px;font-size:13px;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:'IBM Plex Mono',monospace;" />
          </label>
        </div>

        <div style="display:flex;flex-direction:column;gap:8px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Deadline (WAT)</span>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            <button type="button" data-preset="24" style="padding:6px 10px;font-size:12px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">+24 h</button>
            <button type="button" data-preset="48" style="padding:6px 10px;font-size:12px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">+48 h</button>
            <button type="button" data-preset="72" style="padding:6px 10px;font-size:12px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">+72 h</button>
          </div>
          <input type="datetime-local" name="deadline" required
                 style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;" />
          <div id="adm2-retest-deadline-preview" style="font-size:11px;color:${_MODAL_MUTED};font-family:'IBM Plex Mono',monospace;"></div>
        </div>

        <div style="display:flex;gap:10px;">
          <label style="flex:1;display:flex;flex-direction:column;gap:6px;">
            <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Questions (5–15)</span>
            <input type="number" name="n_questions" min="5" max="15" value="10"
                   style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:'IBM Plex Mono',monospace;" />
          </label>
          <label style="flex:1;display:flex;flex-direction:column;gap:6px;">
            <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Window (seconds)</span>
            <input type="number" name="window_s" min="30" max="1800" value="180"
                   style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:'IBM Plex Mono',monospace;" />
          </label>
        </div>

        <div style="padding:12px 14px;background:${_MODAL_BG_ALT};border-left:3px solid #B7A5F5;border-radius:6px;font-size:12px;color:${_MODAL_MUTED};line-height:1.55;">
          Sending this quiz does NOT change the user's account state. Unlocking (or keeping locked) is a separate step via the account-state control above.
        </div>

        <div id="adm2-retest-send-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>

        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">Cancel</button>
          <button type="submit" id="adm2-retest-send-submit"
                  style="padding:9px 18px;font-size:13px;font-weight:700;background:${_MODAL_BRAND};border:1px solid ${_MODAL_BRAND};color:#fff;border-radius:6px;cursor:pointer;">Send</button>
        </div>
      </form>
    </div>
  `;

  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

  const form = overlay.querySelector('#adm2-retest-send-form');
  const deadlineInput = form.querySelector('input[name="deadline"]');
  const preview = overlay.querySelector('#adm2-retest-deadline-preview');
  const updatePreview = () => {
    const iso = datetimeLocalToIso(deadlineInput.value);
    preview.textContent = iso ? `= ${formatLagosDeadline(iso)}` : '';
  };
  deadlineInput.addEventListener('input', updatePreview);
  // Default the input to +48h.
  deadlineInput.value = _toDatetimeLocalValue(new Date(preset48));
  updatePreview();
  overlay.querySelectorAll('button[data-preset]').forEach((b) => {
    b.addEventListener('click', () => {
      const hours = Number(b.dataset.preset);
      const iso = hours === 24 ? preset24 : hours === 48 ? preset48 : preset72;
      deadlineInput.value = _toDatetimeLocalValue(new Date(iso));
      updatePreview();
    });
  });

  const pickSelect = overlay.querySelector('select[name="source_pick"]');
  const customBox = overlay.querySelector('#adm2-retest-custom-source');
  if (pickSelect && customBox) {
    pickSelect.addEventListener('change', () => {
      customBox.style.display = pickSelect.value === 'custom' ? '' : 'none';
    });
  }

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const submit = form.querySelector('#adm2-retest-send-submit');
    const errBox = overlay.querySelector('#adm2-retest-send-error');
    errBox.style.display = 'none';

    const pick = pickSelect ? pickSelect.value : 'custom';
    let source_kind;
    let source_id;
    if (pick && pick.startsWith('live:')) {
      source_kind = 'live';
      source_id = pick.slice('live:'.length);
    } else {
      source_kind = form.custom_kind ? form.custom_kind.value : 'live';
      source_id = form.custom_id ? form.custom_id.value.trim() : '';
    }
    const deadline_at = datetimeLocalToIso(form.deadline.value);
    const n_questions = Number(form.n_questions.value);
    const window_s = Number(form.window_s.value);
    if (!source_id) {
      errBox.textContent = 'A source game id is required.';
      errBox.style.display = '';
      return;
    }
    if (!deadline_at) {
      errBox.textContent = 'A deadline is required.';
      errBox.style.display = '';
      return;
    }
    submit.disabled = true;
    submit.textContent = 'Sending…';
    try {
      const res = await adminSendRetest({
        user_id: userId, source_kind, source_id, deadline_at, n_questions, window_s,
      });
      const served = res.n_questions_served;
      const requested = res.n_questions_requested;
      const shortfallNote = served < requested ? ` (only ${served} of ${requested} — the category was thin)` : '';
      toast.success(`Re-test sent to ${userEmail}${shortfallNote}.`);
      close();
      await renderPlayerDetail(host, ctx);
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Send failed.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = 'Send';
    }
  });

  document.body.appendChild(overlay);
}

/**
 * `datetime-local` inputs expect `YYYY-MM-DDTHH:mm` in LOCAL time. We
 * convert an absolute UTC Date to that format, letting the browser
 * paint the correct local wall-clock time.
 */
function _toDatetimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// Extend deadline modal
// ---------------------------------------------------------------------------

function _openExtendRetestModal({ host, ctx, retestId, onDone }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  });
  overlay.innerHTML = `
    <div style="max-width:460px;width:100%;overflow:hidden;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:20px 22px;border-bottom:1px solid ${_MODAL_BORDER};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Extend deadline</div>
            <div style="font-size:14px;color:${_MODAL_INK};margin-top:4px;font-family:'IBM Plex Mono',monospace;">${escape(retestId)}</div>
          </div>
          <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
        </div>
      </div>
      <form id="adm2-retest-extend-form" style="padding:20px 22px;display:flex;flex-direction:column;gap:14px;">
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">New deadline (WAT)</span>
          <input type="datetime-local" name="deadline" required
                 style="padding:9px 10px;font-size:14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;" />
          <div id="adm2-retest-extend-preview" style="font-size:11px;color:${_MODAL_MUTED};font-family:'IBM Plex Mono',monospace;"></div>
        </label>
        <label style="display:flex;flex-direction:column;gap:6px;">
          <span style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Note (audit trail)</span>
          <textarea name="note" required minlength="1" maxlength="500" rows="2"
                    placeholder="e.g. user requested 24h more, exam commitments"
                    style="padding:9px 10px;font-size:13px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;resize:vertical;font-family:inherit;line-height:1.4;"></textarea>
        </label>
        <div id="adm2-retest-extend-error" style="display:none;padding:8px 10px;border-radius:6px;background:rgba(248,113,113,0.14);border:1px solid rgba(248,113,113,0.35);color:#F87171;font-size:12.5px;"></div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:6px;">
          <button type="button" data-close
                  style="padding:9px 16px;font-size:13px;background:transparent;border:1px solid ${_MODAL_BORDER};color:${_MODAL_MUTED};border-radius:6px;cursor:pointer;">Cancel</button>
          <button type="submit" id="adm2-retest-extend-submit"
                  style="padding:9px 18px;font-size:13px;font-weight:700;background:${_MODAL_BRAND};border:1px solid ${_MODAL_BRAND};color:#fff;border-radius:6px;cursor:pointer;">Extend</button>
        </div>
      </form>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

  const form = overlay.querySelector('#adm2-retest-extend-form');
  const deadlineInput = form.querySelector('input[name="deadline"]');
  const preview = overlay.querySelector('#adm2-retest-extend-preview');
  const updatePreview = () => {
    const iso = datetimeLocalToIso(deadlineInput.value);
    preview.textContent = iso ? `= ${formatLagosDeadline(iso)}` : '';
  };
  deadlineInput.value = _toDatetimeLocalValue(new Date(Date.now() + 48 * 3600 * 1000));
  deadlineInput.addEventListener('input', updatePreview);
  updatePreview();

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const submit = form.querySelector('#adm2-retest-extend-submit');
    const errBox = overlay.querySelector('#adm2-retest-extend-error');
    errBox.style.display = 'none';
    const iso = datetimeLocalToIso(form.deadline.value);
    if (!iso) {
      errBox.textContent = 'Deadline is required.';
      errBox.style.display = '';
      return;
    }
    submit.disabled = true;
    submit.textContent = 'Extending…';
    try {
      await adminExtendRetest(retestId, { deadline_at: iso, note: form.note.value.trim() });
      toast.success('Deadline extended.');
      close();
      if (typeof onDone === 'function') await onDone();
    } catch (err) {
      errBox.textContent = (err && err.message) || 'Extend failed.';
      errBox.style.display = '';
      submit.disabled = false;
      submit.textContent = 'Extend';
    }
  });

  void ctx; void host;
  document.body.appendChild(overlay);
}

// ---------------------------------------------------------------------------
// Detail + review modal
// ---------------------------------------------------------------------------

async function _openRetestDetailModal({ host, ctx, retestId, onDone }) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(4,8,16,0.75);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto;';
  const close = () => overlay.remove();
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  });
  overlay.innerHTML = `
    <div style="max-width:780px;width:100%;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:${_MODAL_INK};">
      <div style="padding:18px 22px;border-bottom:1px solid ${_MODAL_BORDER};display:flex;justify-content:space-between;align-items:center;gap:12px;">
        <div style="font-size:14px;font-weight:600;">Re-test detail</div>
        <button type="button" data-close style="padding:6px 12px;font-size:13px;background:${_MODAL_TRACK};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;cursor:pointer;">✕</button>
      </div>
      <div id="adm2-retest-detail-body" style="padding:18px 22px;max-height:calc(100vh - 120px);overflow-y:auto;">
        <div style="color:${_MODAL_MUTED};font-size:13px;">Loading…</div>
      </div>
    </div>
  `;
  overlay.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));
  document.body.appendChild(overlay);

  let d;
  try {
    d = await adminGetRetest(retestId);
  } catch (err) {
    overlay.querySelector('#adm2-retest-detail-body').innerHTML = `<div style="color:#F87171;font-size:13px;">${escape(err.message || 'Load failed')}</div>`;
    return;
  }

  overlay.querySelector('#adm2-retest-detail-body').innerHTML = _retestDetailHtml(d);

  // Wire the verdict buttons.
  overlay.querySelectorAll('button[data-verdict]').forEach((b) => {
    b.addEventListener('click', async () => {
      const outcome = b.dataset.verdict;
      const noteEl = overlay.querySelector('#adm2-retest-review-note');
      const note = (noteEl && noteEl.value.trim()) || '';
      b.disabled = true;
      try {
        const res = await adminReviewRetest(retestId, { review_outcome: outcome, note });
        const stateNote = res.account_unchanged
          ? ` Account state (${res.user_account_state}) is unchanged — use the account-state control above to unlock or keep restricted.`
          : '';
        toast.success(`Marked ${outcome}.${stateNote}`);
        close();
        if (typeof onDone === 'function') await onDone();
      } catch (err) {
        toast.error(err.message || 'Review failed.');
        b.disabled = false;
      }
    });
  });
  void ctx; void host;
}

function _retestDetailHtml(d) {
  const bandChip = d.comparison_band ? _retestBandChip(d.comparison_band) : '';
  const verdict = d.review_outcome
    ? `<span style="padding:5px 10px;border-radius:999px;background:rgba(139,107,242,.14);color:#B7A5F5;border:1px solid rgba(139,107,242,.4);font:700 11px 'IBM Plex Sans',sans-serif;text-transform:uppercase;letter-spacing:.05em;">Marked ${escape(d.review_outcome)}</span>`
    : '';
  const acc = (d.original_accuracy != null && d.retest_accuracy != null)
    ? `<div style="font:600 20px 'IBM Plex Mono',monospace;color:${_MODAL_INK};margin-top:6px;">${(d.original_accuracy * 100).toFixed(0)}% → ${(d.retest_accuracy * 100).toFixed(0)}%</div>`
    : '';
  const avgMs = (d.original_avg_ms != null && d.retest_avg_ms != null)
    ? `<div style="font-size:11px;color:${_MODAL_MUTED};margin-top:4px;">avg ${d.original_avg_ms}ms → ${d.retest_avg_ms}ms</div>`
    : '';

  const agg = d.integrity_aggregates || {};
  const aggChips = _integrityChips(agg);
  const integrityWarning = (agg.any_untrusted || agg.decoy_hit || agg.overlay_detected)
    ? `<div style="margin-top:12px;padding:12px 14px;background:rgba(248,113,113,.10);border-left:3px solid #F87171;border-radius:6px;font-size:12.5px;color:${_MODAL_INK};line-height:1.55;">
         <b style="color:#F87171;">Bot-run adjudication warning.</b>
         A high re-test score achieved with <span style="font-family:'IBM Plex Mono',monospace;">is_trusted=false</span> /
         <span style="font-family:'IBM Plex Mono',monospace;">decoy_hit=true</span> / an overlay observed is <em>worse</em> evidence
         than a low score — it means the adjudication quiz itself was botted.
       </div>`
    : '';

  const perAnswer = (d.per_answer || []).map(_perAnswerRowHtml).join('');
  const originalRows = (d.original_answers || []).map(_originalAnswerRowHtml).join('');
  const historyItems = (d.deadline_history || []).map((h) => `
    <div style="font-size:12px;color:${_MODAL_MUTED};font-family:'IBM Plex Mono',monospace;">
      +extend by ${escape(h.by_admin_id || '?')} · ${escape(formatLagosDeadline(h.new))} · ${escape(h.note || '')}
    </div>
  `).join('');

  const canRecord = (d.status === 'submitted' || d.status === 'expired') && !d.review_outcome;
  const verdictArea = canRecord
    ? `
      <div style="margin-top:20px;padding:16px 18px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:10px;">
        <div style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;margin-bottom:10px;">Record verdict (advisory — does NOT change account_state)</div>
        <textarea id="adm2-retest-review-note" placeholder="Reasoning + anything to email the user separately…"
                  style="width:100%;min-height:60px;padding:9px 10px;font-size:13px;background:${_MODAL_BG};border:1px solid ${_MODAL_BORDER};color:${_MODAL_INK};border-radius:6px;font-family:inherit;line-height:1.4;resize:vertical;"></textarea>
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
          <button type="button" data-verdict="passed"
            style="padding:9px 16px;font-size:13px;font-weight:700;background:rgba(74,222,128,.14);border:1px solid rgba(74,222,128,.4);color:#4ADE80;border-radius:6px;cursor:pointer;">Mark passed</button>
          <button type="button" data-verdict="failed"
            style="padding:9px 16px;font-size:13px;font-weight:700;background:rgba(248,113,113,.14);border:1px solid rgba(248,113,113,.4);color:#F87171;border-radius:6px;cursor:pointer;">Mark failed</button>
          <button type="button" data-verdict="inconclusive"
            style="padding:9px 16px;font-size:13px;font-weight:700;background:rgba(251,191,36,.14);border:1px solid rgba(251,191,36,.4);color:#FBBF24;border-radius:6px;cursor:pointer;">Inconclusive</button>
        </div>
        <div style="margin-top:10px;font-size:11px;color:${_MODAL_MUTED};line-height:1.5;">
          Current account state: <b style="font-family:'IBM Plex Mono',monospace;color:${_MODAL_INK};">${escape(d.user_account_state || 'unknown')}</b>.
          Recording a verdict here does NOT unlock or ban — do that separately from the account-state control on the player page.
        </div>
      </div>
    `
    : '';

  return `
    <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;">
      <span style="padding:5px 10px;border-radius:999px;font:700 11px 'IBM Plex Sans',sans-serif;text-transform:uppercase;letter-spacing:.05em;${_retestStatusStyle(d.status)}">${escape(d.status)}</span>
      ${bandChip}
      ${verdict}
      <span style="font-family:'IBM Plex Mono',monospace;font-size:12px;color:${_MODAL_MUTED};">${escape(d.source_kind)}:${escape((d.source_id || '').slice(0, 8))}</span>
    </div>

    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;margin-top:16px;">
      <div style="padding:12px 14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:10px;">
        <div style="font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Accuracy (original → re-test)</div>
        ${acc}
        ${avgMs}
      </div>
      <div style="padding:12px 14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:10px;">
        <div style="font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Deadline</div>
        <div style="font-size:13px;color:${_MODAL_INK};margin-top:6px;">${escape(formatLagosDeadline(d.deadline_at))}</div>
        <div style="font-size:11px;color:${_MODAL_MUTED};margin-top:4px;">extended ${escape(String(d.deadline_extended_count || 0))} time(s)</div>
        ${historyItems ? `<div style="margin-top:8px;">${historyItems}</div>` : ''}
      </div>
      <div style="padding:12px 14px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:10px;">
        <div style="font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;">Client integrity signals</div>
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;">${aggChips}</div>
      </div>
    </div>

    ${integrityWarning}

    <div style="margin-top:20px;">
      <div style="font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;margin-bottom:8px;">Original game — user's answers</div>
      <div style="display:flex;flex-direction:column;gap:6px;">${originalRows || `<div style="color:${_MODAL_MUTED};font-size:12px;">(no per-answer detail available)</div>`}</div>
    </div>

    <div style="margin-top:20px;">
      <div style="font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:${_MODAL_FAINT};font-weight:700;margin-bottom:8px;">Re-test — user's answers</div>
      <div style="display:flex;flex-direction:column;gap:6px;">${perAnswer}</div>
    </div>

    ${verdictArea}
  `;
}

function _integrityChips(agg) {
  const chip = (label, on, style) => `
    <span style="padding:3px 8px;border-radius:999px;font:700 10px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;${on ? style : `background:rgba(148,163,184,.08);color:${_MODAL_FAINT};border:1px solid ${_MODAL_BORDER};`}">${escape(label)}</span>
  `;
  return [
    chip('is_trusted', agg.any_untrusted === false, 'background:rgba(74,222,128,.12);color:#4ADE80;border:1px solid rgba(74,222,128,.35);'),
    chip('untrusted click', !!agg.any_untrusted, 'background:rgba(248,113,113,.14);color:#F87171;border:1px solid rgba(248,113,113,.4);'),
    chip('decoy hit', !!agg.decoy_hit, 'background:rgba(248,113,113,.14);color:#F87171;border:1px solid rgba(248,113,113,.4);'),
    chip('overlay', !!agg.overlay_detected, 'background:rgba(248,113,113,.14);color:#F87171;border:1px solid rgba(248,113,113,.4);'),
    chip(`vis-changes ${agg.visibility_changes || 0}`, (agg.visibility_changes || 0) > 0, 'background:rgba(251,191,36,.14);color:#FBBF24;border:1px solid rgba(251,191,36,.4);'),
  ].join('');
}

function _perAnswerRowHtml(a) {
  const correct = a.is_correct;
  const picked = a.picked_index == null ? '—' : String.fromCharCode(65 + Number(a.picked_index));
  const correctL = String.fromCharCode(65 + Number(a.correct_index));
  const flagStyle = correct ? 'color:#4ADE80;' : 'color:#F87171;';
  const untrusted = a.signals && a.signals.is_trusted === false;
  const decoy = a.signals && a.signals.decoy_hit === true;
  const flags = [
    untrusted ? '<span style="padding:2px 6px;border-radius:4px;background:rgba(248,113,113,.16);color:#F87171;border:1px solid rgba(248,113,113,.4);font-size:10px;font-family:\'IBM Plex Mono\',monospace;">untrusted</span>' : '',
    decoy ? '<span style="padding:2px 6px;border-radius:4px;background:rgba(248,113,113,.16);color:#F87171;border:1px solid rgba(248,113,113,.4);font-size:10px;font-family:\'IBM Plex Mono\',monospace;">decoy</span>' : '',
  ].filter(Boolean).join(' ');
  return `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:9px 12px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:8px;">
      <span style="font-family:'IBM Plex Mono',monospace;font-size:11px;color:${_MODAL_MUTED};min-width:24px;">Q${a.order + 1}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;color:${_MODAL_INK};line-height:1.4;overflow-wrap:anywhere;">${escape(a.prompt || '')}</div>
        <div style="font-size:11px;color:${_MODAL_MUTED};margin-top:3px;font-family:'IBM Plex Mono',monospace;">
          picked <b style="${flagStyle}">${escape(picked)}</b> · correct <b style="color:#4ADE80;">${escape(correctL)}</b> · ${a.ms}ms ${flags}
        </div>
      </div>
    </div>
  `;
}

function _originalAnswerRowHtml(a) {
  const picked = a.picked_index == null ? '—' : String.fromCharCode(65 + Number(a.picked_index));
  const correctL = a.correct_index == null ? '—' : String.fromCharCode(65 + Number(a.correct_index));
  const flagStyle = a.is_correct ? 'color:#4ADE80;' : 'color:#F87171;';
  return `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:9px 12px;background:${_MODAL_BG_ALT};border:1px solid ${_MODAL_BORDER};border-radius:8px;">
      <span style="font-family:'IBM Plex Mono',monospace;font-size:11px;color:${_MODAL_MUTED};min-width:24px;">Q${a.order + 1}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;color:${_MODAL_INK};line-height:1.4;overflow-wrap:anywhere;">${escape(a.prompt || '')}</div>
        <div style="font-size:11px;color:${_MODAL_MUTED};margin-top:3px;font-family:'IBM Plex Mono',monospace;">
          picked <b style="${flagStyle}">${escape(picked)}</b> · correct <b style="color:#4ADE80;">${escape(correctL)}</b> · ${a.ms}ms
        </div>
      </div>
    </div>
  `;
}
